import {
  ApiRequest,
  ApiResponse,
  JobEventScheduleMailKey,
  EditingJobEventScheduleType
} from "@/api/types";
import { ref, InjectionKey, computed } from "@vue/composition-api";
import _ from "lodash";
import {
  DisplayDescriptionItem,
  fixEventForCreate,
  fixEventForUpdate,
  formConfig,
  REQUIRED_DESCRIPTION_ITEMS,
  OPTIONAL_DEFAULT_DESCRIPTION_ITEMS,
  ValidateKey,
  hasForbiddenUrl,
  hasWarnUrl,
  findDuplicateIds,
  warnUrlMessage,
  isNotWebMeetingUrl,
  warnWebMeetingMessage,
  forbiddenUrlErrorMessage,
  unsupportedSymbolErrorMessage,
  warnCompetitorTextMessage,
  editableJobEventData,
  getEventCategoryText
} from "../jobEvent";
import {
  includesUnsupportedSymbols,
  includesEmoji
} from "../invalid-characters-helper";
import { useValidate } from "./useValidate";
import {
  getJobEvent as getJobEventRequest,
  submitJobEvent as submitJobEventRequest,
  updateJobEvent as updateJobEventRequest,
  withdrawJobEventSubmission as withdrawJobEventSubmissionRequest,
  getProhibitedWords as getProhibitedWordsRequest
} from "@/api";
import { useComponentInstance } from "./useComponentInstance";
import {
  checkAtsApproved,
  getContractInfoAndStatusById
} from "../contract-helper";
import { getRandomId } from "../math-helper";
import { mergeArraysWithoutDuplicates } from "../array-helper";
import {
  isDateBeforeCurrentDate,
  isDateAfterSpecificDate,
  isSameDateAndMinute
} from "@/helpers/date-helper";
import { hasApplicationUrlApprovedPolicy } from "@/helpers/auth-helper";

export type JobEventStore = ReturnType<typeof useJobEvent>;

export const JOB_EVENT_STORE_KEY: InjectionKey<JobEventStore> = Symbol(
  "JOB_EVENT_STORE_KEY"
);

export type ValidateSubKey = string | number | undefined;
export type JobEventError = {
  key: ValidateKey;
  subKey: ValidateSubKey; // 項目が一緒でも複数入力欄がある時の識別用
  message: string;
};
export type JobEventWarns = {
  key: ValidateKey;
  subKey: ValidateSubKey;
  message: string;
};

/**
 * フロントでチェックするバリデーションエラーのインスタンス
 */
export class ValidationError extends Error {
  errors: JobEventError[];
  constructor(errors: JobEventError[], message?: string) {
    super(message);
    this.errors = errors;
  }
}

/**
 * JobEventの編集情報、エラー情報をグローバルで保持する
 */
export const useJobEvent = ({
  eventId: defaultEventId
}: {
  eventId: number | undefined;
}) => {
  const root = useComponentInstance();
  const originalEvent = ref<ApiRequest.EditingDraftJobEvent>();
  const editingJobEvent = ref<ApiRequest.EditingDraftJobEvent>();
  const editingJobEventSchedules = ref<ApiResponse.JobEventSchedules>();
  const comments = ref<ApiResponse.JobEvent["comments"]>();
  const promotionContracts = ref<ApiResponse.JobEvent["promotion_contracts"]>();
  const jobEventErrors = ref<JobEventError[]>([]);
  // submit時に全入力項目をチェックし、warningを表示するケースに該当した場合にメッセージ等が詰められるが、現状リアルタイムのチュック以外にwarningを表示していないので使われていない
  const jobEventWarns = ref<JobEventWarns[]>([]);
  const uncroppedImage = ref<string>();
  const requiredDescriptionItemIds = ref<number[]>([]);
  const optionalDefaultDescriptionItemIds = ref<number[]>([]);
  const repeatedDescriptionItemIds = ref<number[]>([]);

  const partnerUser = computed<ApiResponse.PartnerUser | undefined>(
    () => root.$store.state.domains.user.partnerUser
  );

  // NOTE: 新規作成時は event_id が後から追加されるため、computedで定義する
  const eventId = computed(() => {
    return editingJobEvent.value?.event_id || defaultEventId;
  });
  const isNewJobEvent = computed(() => {
    return !eventId.value;
  });

  /**
   * 表示用の descriptionItems
   */
  const disabledEdit = computed(
    () =>
      editingJobEvent.value?.approval_status === "pending" ||
      editingJobEvent.value?.approval_status === "waiting_for_publish"
  );

  /**
   * 受付方法の入力を制御するcomputed
   */
  const disabledReceptionTypeEdit = computed(() => {
    const hasPolicy = hasApplicationUrlApprovedPolicy(
      editingJobEvent.value?.contract_id
    );
    if (!hasPolicy) {
      if (editingJobEvent.value) {
        editingJobEvent.value.reception_type = "internal";
      }
    }
    return !hasPolicy;
  });

  /**
   * 表示用の descriptionItems
   */
  const displayDescriptionItems = computed<DisplayDescriptionItem[]>(() => {
    const sortedDescriptionItems = [
      ...(editingJobEvent.value?.event_description_items || [])
    ].sort((a, b) => a.position - b.position);

    return sortedDescriptionItems.filter(
      item => item.label !== formConfig["attention_item"].label
    );
  });

  const wordData = ref<ApiResponse.ProhibitedWords>();
  const getWords = async () => {
    const { data } = await getProhibitedWordsRequest();
    wordData.value = data;
  };
  getWords();

  /**
   * 不足している必須 descriptionItems がある場合、デフォルト情報を追加する
   */
  const setLackedRequiredDescriptionItems = () => {
    const lackedItems = REQUIRED_DESCRIPTION_ITEMS.filter(
      requiredItem =>
        !editingJobEvent.value?.event_description_items.find(
          item => requiredItem.label === item.label
        )
    );

    const additionalItems: DisplayDescriptionItem[] = lackedItems.map(
      (item, index) => ({
        id: getRandomId(),
        label: item.label,
        content: item.defaultContent,
        position: displayDescriptionItems.value.length + (index + 1)
      })
    );

    const nextItems = [...displayDescriptionItems.value, ...additionalItems];
    const firstAppearance: { [key: string]: number } = {};

    nextItems.forEach(item => {
      if (editableJobEventData.includes(item.label)) {
        if (firstAppearance[item.label] === undefined) {
          firstAppearance[item.label] = item.id;
        } else {
          repeatedDescriptionItemIds.value.push(item.id);
        }
      }
    });

    // nextItemsの中から、不要な項目を削除する
    if (editingJobEvent.value) {
      setEditingJobEvent({
        ...editingJobEvent.value,
        event_description_items: nextItems
      });

      requiredDescriptionItemIds.value = mergeArraysWithoutDuplicates(
        requiredDescriptionItemIds.value,
        additionalItems.map(item => item.id)
      );
    }
  };

  /**
   * 不足しているデフォルト任意項目（定員・会場）がある場合、追加する
   */
  const setLackedOptionalDefaultDescriptionItems = () => {
    const lackedItems = OPTIONAL_DEFAULT_DESCRIPTION_ITEMS.filter(
      optionalDefaultItem =>
        !editingJobEvent.value?.event_description_items.find(
          item => optionalDefaultItem.label === item.label
        )
    );

    const additionalItems: DisplayDescriptionItem[] = lackedItems.map(
      (item, index) => ({
        id: getRandomId(),
        label: item.label,
        content: item.defaultContent,
        position: displayDescriptionItems.value.length + (index + 1)
      })
    );

    const nextItems = [...displayDescriptionItems.value, ...additionalItems];
    if (editingJobEvent.value) {
      setEditingJobEvent({
        ...editingJobEvent.value,
        event_description_items: nextItems
      });

      optionalDefaultDescriptionItemIds.value = mergeArraysWithoutDuplicates(
        optionalDefaultDescriptionItemIds.value,
        additionalItems.map(item => item.id)
      );
    }
  };

  const {
    formErrors,
    getFirstErrorMessage,
    resetErrorMessage,
    validate,
    validateDateRange
  } = useValidate();

  const fixedDraftEvent = computed(() => {
    if (isNewJobEvent.value && editingJobEvent.value) {
      return fixEventForCreate(editingJobEvent.value);
    }

    if (!isNewJobEvent.value && editingJobEvent.value && originalEvent.value) {
      return fixEventForUpdate(editingJobEvent.value, originalEvent.value);
    }
  });

  const formatDraftEventForEdit = (
    draftEvent: ApiResponse.JobEvent["draft_event"]
  ): ApiRequest.EditingDraftJobEvent => {
    const editingJobEventSchedules = draftEvent.schedules.map(schedule => {
      let scheduleType: EditingJobEventScheduleType = "offline";

      const isOnlineSchedule = !!schedule.live_url;

      if (isOnlineSchedule) {
        scheduleType = "online";
      }

      return {
        ...schedule,
        schedule_type: scheduleType
      };
    });

    return {
      ...draftEvent,
      schedules: editingJobEventSchedules
    };
  };

  const fetching = ref(false);
  const refreshEditingJobEvent = async () => {
    if (!eventId.value) {
      throw new Error("eventIdが見つかりません");
    }

    try {
      fetching.value = true;
      const { data: currentJobEvent } = await getJobEventRequest({
        eventId: eventId.value
      });

      const formattedCurrentJobEvent = formatDraftEventForEdit(
        currentJobEvent.draft_event
      );

      setEditingJobEvent(formattedCurrentJobEvent);
      originalEvent.value = currentJobEvent.draft_event;
      comments.value = currentJobEvent.comments;
      promotionContracts.value = currentJobEvent.promotion_contracts;

      const requiredDescriptionItems = currentJobEvent.draft_event.event_description_items.filter(
        item =>
          REQUIRED_DESCRIPTION_ITEMS.some(
            requiredItem => requiredItem.label === item.label
          )
      );

      requiredDescriptionItemIds.value = mergeArraysWithoutDuplicates(
        requiredDescriptionItemIds.value,
        requiredDescriptionItems.map(item => item.id)
      );

      const optionalDefaultDescriptionItems = currentJobEvent.draft_event.event_description_items.filter(
        item =>
          OPTIONAL_DEFAULT_DESCRIPTION_ITEMS.some(
            requiredItem => requiredItem.label === item.label
          )
      );

      optionalDefaultDescriptionItemIds.value = mergeArraysWithoutDuplicates(
        optionalDefaultDescriptionItemIds.value,
        optionalDefaultDescriptionItems.map(item => item.id)
      );
    } finally {
      fetching.value = false;
    }
  };

  const updating = ref(false);

  const jobEventDraftValidate = (key: ValidateKey, subKey?: ValidateSubKey) => {
    jobEventValidate(key, subKey, true);
  };

  const saveJobEvent = async () => {
    if (!fixedDraftEvent.value) {
      return;
    }

    resetJobEventErrors();

    jobEventDraftValidate("title");
    jobEventDraftValidate("eye_catch_image");
    jobEventDraftValidate("preferred_publish_at");
    jobEventDraftValidate("application_url");
    editingJobEvent.value?.event_description_items.forEach(item => {
      jobEventDraftValidate("event_description_items/label", item.id);
      jobEventDraftValidate("event_description_items/content", item.id);
    });
    editingJobEvent.value?.schedules.forEach(schedule => {
      jobEventDraftValidate("schedules/title", schedule.id);
      jobEventDraftValidate("schedules/time_limit_at", schedule.id);
      jobEventDraftValidate("schedules/start_at", schedule.id);
      jobEventDraftValidate("schedules/end_at", schedule.id);
      jobEventDraftValidate("schedules/live_url", schedule.id);
      jobEventDraftValidate("schedules/place", schedule.id);
      jobEventDraftValidate("schedules/limited_place", schedule.id);
    });

    if (jobEventErrors.value.length) {
      throw new ValidationError(jobEventErrors.value);
    }

    try {
      updating.value = true;
      const { data } = await updateJobEventRequest({
        draft_event: fixedDraftEvent.value
      });

      if (editingJobEvent.value) {
        setEditingJobEvent({
          ...editingJobEvent.value,
          id: data.id,
          event_id: data.event_id
        });
      }

      /**
       * データの乖離が生まれる可能性があるため、最新の下書きをgetする
       */
      await refreshEditingJobEvent();
    } finally {
      updating.value = false;
    }
  };

  /**
   * 掲載依頼時にデフォルト任意項目（定員・会場）が空なら除外する
   */
  const omitOptionalDefaultDescriptionItems = (
    fixedDraftEvent: ApiRequest.JobEvent["draft_event"]
  ): ApiRequest.JobEvent["draft_event"] => {
    let [itemsForSubmit, itemsForDelete] = _.partition(
      fixedDraftEvent.event_description_items,
      eventDescriptionItem => {
        // 定員・会場が空なら、入稿内容から除外する
        let isNotOptionalDefaultDescriptionItem = OPTIONAL_DEFAULT_DESCRIPTION_ITEMS.every(
          item => item.label !== eventDescriptionItem.label
        );

        return (
          isNotOptionalDefaultDescriptionItem || !!eventDescriptionItem.content
        );
      }
    );

    itemsForDelete = itemsForDelete.map(item => ({ ...item, _destroy: true }));

    return {
      ...fixedDraftEvent,
      event_description_items: [...itemsForSubmit, ...itemsForDelete]
    };
  };

  const submitting = ref(false);
  const submitJobEvent = async () => {
    if (!fixedDraftEvent.value) {
      return;
    }

    validateAllProperties();
    if (jobEventErrors.value.length) {
      throw new ValidationError(jobEventErrors.value);
    }

    const complectedDraftEvent = omitOptionalDefaultDescriptionItems(
      fixedDraftEvent.value as ApiRequest.JobEvent["draft_event"]
    );

    try {
      submitting.value = true;
      const { data } = await submitJobEventRequest({
        draft_event: complectedDraftEvent,
        comments: comments.value!
      });

      if (isNewJobEvent.value && editingJobEvent.value) {
        setEditingJobEvent({
          ...editingJobEvent.value,
          id: data.draft_event_id,
          event_id: data.event_id
        });
      }

      /**
       * データの乖離が生まれる可能性があるため、最新の下書きをgetする
       */
      await refreshEditingJobEvent();
    } finally {
      submitting.value = false;
    }
  };

  const withdrawing = ref(false);
  const withdrawJobEvent = async () => {
    if (!fixedDraftEvent.value) {
      return;
    }
    try {
      withdrawing.value = true;
      await withdrawJobEventSubmissionRequest({
        eventId: fixedDraftEvent.value.event_id!
      });
      /**
       * データの乖離が生まれる可能性があるため、最新の下書きをgetする
       */
      await refreshEditingJobEvent();
    } finally {
      withdrawing.value = false;
    }
  };

  const loading = computed(
    () =>
      fetching.value || updating.value || submitting.value || withdrawing.value
  );

  const setEditingJobEvent = (
    jobEvent: ApiRequest.EditingDraftJobEvent,
    validateKey?: { key: ValidateKey; subKey?: ValidateSubKey }
  ) => {
    editingJobEvent.value = jobEvent;
    if (validateKey) {
      jobEventValidate(validateKey.key, validateKey.subKey);
    }
  };

  /**
   * 特定の説明文のエラーを削除
   */
  const resetDescriptionError = (descriptionId: number) => {
    (Object.keys(formConfig) as ValidateKey[]).forEach(key => {
      const splittedKey = key.split("/")[0];

      if (key.includes("/") && splittedKey === "event_description_items") {
        setJobEventErrors(key, descriptionId, undefined);
      }
    });
  };

  /**
   * 特定のスケジュールのエラーを削除
   */
  const resetScheduleError = (scheduleId: number) => {
    (Object.keys(formConfig) as ValidateKey[]).forEach(key => {
      const splittedKey = key.split("/")[0];

      if (key.includes("/") && splittedKey === "schedules") {
        setJobEventErrors(key, scheduleId, undefined);
      }
    });
  };

  /**
   * レジュメスケジュールのエラーを削除
   */
  const resetResumeError = (resumeId: number) => {
    (Object.keys(formConfig) as ValidateKey[]).forEach(key => {
      const splittedKey = key.split("/")[0];

      if (key.includes("/") && splittedKey === "resume_questions") {
        setJobEventErrors(key, resumeId, undefined);
      }
    });
  };

  const validateAllProperties = () => {
    (Object.keys(formConfig) as ValidateKey[]).forEach(key => {
      if (!key.includes("/")) {
        jobEventValidate(key);
      } else {
        const splittedKey = key.split("/")[0];

        switch (splittedKey) {
          case "event_description_items":
            editingJobEvent.value?.event_description_items.forEach(item => {
              jobEventValidate(key, item.id);
            });
            break;

          case "schedules":
            editingJobEvent.value?.schedules.forEach(schedule => {
              jobEventValidate(key, schedule.id);
            });
            break;

          case "resume_questions":
            editingJobEvent.value?.resume_questions.forEach(resume => {
              jobEventValidate(key, resume.id);
            });
            break;

          default:
            break;
        }
      }
    });
  };

  const validateTextarea = (formLabel: string, text?: string) => {
    if (!text) return;

    if (hasForbiddenUrl(text)) {
      return forbiddenUrlErrorMessage(formLabel);
    }

    if (includesUnsupportedSymbols(text) || includesEmoji(text)) {
      return unsupportedSymbolErrorMessage(formLabel);
    }

    // TODO #3905の実装リリース後にメールアドレスを含む文章が公序良俗に反する言葉が使われるとしてエラーになることがわかった
    //      上記チケットの対応前から意図しない形で公序良俗チェック違反になるとの報告があったことから暫定対応としてチェックを外すことになった
    //      将来的には、エラーではなく警告として表示するように変更する予定
    // c.f. https://onecareer.slack.com/archives/C012PLGR6K0/p1712222048291879?thread_ts=1712209913.817419&cid=C012PLGR6K0
    // c.f. https://onecareer.slack.com/archives/C012PLGR6K0/p1712213328743959
    // if (wordData.value?.dirty_words.some(word => text.includes(word))) {
    //   return dirtyWordErrorMessage(formLabel);
    // }

    return null;
  };

  const handleWarnMessageForTextarea = (
    key: ValidateKey,
    subKey?: ValidateSubKey,
    text?: string
  ) => {
    if (!text) return;

    // NOTE: 以下のラベルが付いた項目（削除不可な項目）は重複エラーが出て保存・入稿できないケースがある
    // 暫定対応として、重複バリデーションを回避する
    // TODO: 仕様から検討
    hasWarnUrl(text)
      ? setJobEventWarns(key, subKey, warnUrlMessage)
      : setJobEventWarns(key, subKey, undefined);
    wordData.value?.competitors_names.some(word => text.includes(word))
      ? setJobEventWarns(key, subKey, warnCompetitorTextMessage)
      : setJobEventWarns(key, subKey, undefined);
  };

  const jobEventValidate = (
    key: ValidateKey,
    subKey?: ValidateSubKey,
    forDraft: boolean = false // NOTE: 下書き保存の場合、必須等一部のエラーは通過してOK
  ) => {
    if (!editingJobEvent.value) {
      return;
    }
    const splittedKey = key.split("/")[0];

    switch (key) {
      case "title": {
        const validationResult = validateTextarea(
          formConfig[key].label,
          editingJobEvent.value.title
        );
        const error = validate(
          editingJobEvent.value.title,
          {
            required: true,
            customErrors: validationResult
              ? [{ message: validationResult }]
              : []
          },
          undefined,
          formConfig[key].label
        );
        handleWarnMessageForTextarea(key, subKey, editingJobEvent.value.title);
        setJobEventErrors(key, subKey, error[0]?.message);
        return error[0]?.message;
      }

      case "eye_catch_image": {
        const uncropped =
          !editingJobEvent.value.eye_catch_image && uncroppedImage.value;

        const errorMessage = uncropped
          ? "画像登録には切り抜く必要があります"
          : undefined;

        setJobEventErrors(key, subKey, errorMessage);
        return errorMessage;
      }

      case "preferred_publish_at": {
        if (!editingJobEvent.value.scheduled_publish) {
          setJobEventErrors(key);
          return;
        }
        const error = validate(
          editingJobEvent.value.preferred_publish_at || undefined,
          {
            required: editingJobEvent.value.scheduled_publish && !forDraft,
            isDateAndTime: true,
            businessDaysLater: forDraft ? undefined : 3
          },
          undefined,
          formConfig[key].label
        );
        setJobEventErrors(key, subKey, error[0]?.message);
        return error[0]?.message;
      }

      case "scheduled_publish": {
        const error = validate(
          editingJobEvent.value.scheduled_publish,
          {
            required: !forDraft
          },
          undefined,
          formConfig[key].label
        );
        setJobEventErrors(key, subKey, error[0]?.message);
        return error[0]?.message;
      }

      case "attention_item":
      case "preferred_updated_at":
      case "approval_status":
      case "public_status":
      case "publish_at":
      case "updated_at":
      case "approved_at":
      case "reject_reason":
      case "comment": {
        return;
      }
      case "category": {
        const error = validate(
          editingJobEvent.value.category,
          {
            required: !forDraft
          },
          undefined,
          formConfig[key].label
        );
        setJobEventErrors(key, subKey, error[0]?.message);
        return error[0]?.message;
      }
      case "contract_id": {
        const error = validate(
          editingJobEvent.value.contract_id,
          {
            required: !forDraft,
            customErrors: (() => {
              const results = [];
              if (editingJobEvent.value.contract_id) {
                const contractInfo = getContractInfoAndStatusById(
                  editingJobEvent.value.contract_id
                );
                // subKeyに"verifyContract"が設定された場合の処理。
                // UX向上のために掲載依頼ボタンの後ではなく、初期ロードでこのバリデーションを実施することで、
                // 契約切れの場合、画面起動時に非公開状態をユーザーに通知するためのバリデーション。
                if (subKey === "verifyContract" && !contractInfo?.isAvailable) {
                  results.push({
                    message:
                      "対象契約の契約期間が終了したため、イベントが公開終了になっています。このイベントを公開したい場合は有効期間内の契約を選択してください。"
                  });
                } else if (!contractInfo?.isAvailable) {
                  // 掲載依頼時に契約が無効であればエラーメッセージを追加する。
                  results.push({
                    message: `有効な${formConfig[key].label}を選択してください。`
                  });
                }
              }
              return results;
            })()
          },
          undefined,
          formConfig[key].label
        );
        setJobEventErrors(key, subKey, error[0]?.message);
        return error[0]?.message;
      }
      case "application_url": {
        const error = validate(
          editingJobEvent.value.application_url,
          {
            isUrl: true,
            required:
              editingJobEvent.value.reception_type === "external" && !forDraft,
            customErrors: [
              ...(hasForbiddenUrl(
                editingJobEvent.value.application_url,
                "hasExclude"
              )
                ? [
                    {
                      message: forbiddenUrlErrorMessage(formConfig[key].label)
                    }
                  ]
                : [])
            ]
          },
          undefined,
          formConfig[key].label
        );
        setJobEventErrors(key, subKey, error[0]?.message);
        return error[0]?.message;
      }
      case "reception_type": {
        const error = validate(
          editingJobEvent.value.reception_type,
          {
            required: !forDraft
          },
          undefined,
          formConfig[key].label
        );
        setJobEventErrors(key, subKey, error[0]?.message);
        return error[0]?.message;
      }
      case "is_lottery": {
        if (!editingJobEvent.value.is_lottery) {
          editingJobEvent.value.schedules.forEach(schedule => {
            setJobEventErrors("schedules/limited_place", schedule.id);
          });
          return;
        }
      }
      case "iweb_code":
      case "iweb_type":
      case "iweb_graduate_year":
      case "mail_notifications/reminder":
      case "mail_notifications/thanks":
        return;

      case "schedules": {
        const errorMessage = !editingJobEvent.value.schedules.length
          ? "スケジュールの作成が必要です"
          : "";
        setJobEventErrors(key, subKey, errorMessage);
        return errorMessage;
      }
    }

    switch (splittedKey) {
      case "event_description_items": {
        if (!subKey) {
          throw "subKey の指定が必須です";
        }
        const targetItem = editingJobEvent.value.event_description_items.find(
          item => item.id === subKey
        );
        if (!targetItem) {
          return;
        }

        // labelとcontentが完全に一致する重複をチェックする
        const ids = findDuplicateIds(
          editingJobEvent.value.event_description_items
        );
        const errorMessage = "内容が重複している項目があります";

        editingJobEvent.value.event_description_items.forEach(item => {
          if (ids.includes(item.id)) {
            setJobEventErrors(key, item.id, errorMessage);
          }
        });

        if (ids.length) {
          return errorMessage;
        }

        switch (key) {
          case "event_description_items/label": {
            handleWarnMessageForTextarea(key, subKey, targetItem?.label);
            const duplicateAllowableLabels = [
              "会場",
              "定員",
              "概要",
              "お問い合わせ先"
            ];
            const labelListToValidate = editingJobEvent.value.event_description_items
              .filter(item => {
                return (
                  item.id !== targetItem.id &&
                  !duplicateAllowableLabels.includes(item.label)
                );
              })
              .map(item => item.label);
            const validationResult = validateTextarea(
              formConfig[key].label,
              targetItem?.label
            );
            const error = validate(
              targetItem?.label,
              {
                required: !forDraft,
                uniqueValues: [
                  formConfig["title"].label,
                  formConfig["eye_catch_image"].label,
                  ...labelListToValidate
                ],
                customErrors: validationResult
                  ? [{ message: validationResult }]
                  : []
              },
              undefined,
              formConfig[key].label
            );
            setJobEventErrors(key, subKey, error[0]?.message);
            return error[0]?.message;
          }
          case "event_description_items/content": {
            handleWarnMessageForTextarea(key, subKey, targetItem?.content);
            if (
              OPTIONAL_DEFAULT_DESCRIPTION_ITEMS.some(
                item => item.label === targetItem?.label
              )
            ) {
              const subject =
                OPTIONAL_DEFAULT_DESCRIPTION_ITEMS.find(
                  item => item.label === targetItem?.label
                )?.label || "";
              const validationResult = validateTextarea(
                subject,
                targetItem?.content
              );
              const error = validate(
                targetItem?.content,
                {
                  customErrors: validationResult
                    ? [{ message: validationResult }]
                    : []
                },
                undefined,
                subject
              );
              setJobEventErrors(key, subKey, error[0]?.message);
              return error[0]?.message;
            }
            const subject = REQUIRED_DESCRIPTION_ITEMS.some(
              item => item.label === targetItem?.label
            )
              ? targetItem?.label
              : formConfig[key].label;
            const validationResult = validateTextarea(
              subject,
              targetItem?.content
            );
            const error = validate(
              targetItem?.content,
              {
                required: !forDraft,
                customErrors: validationResult
                  ? [{ message: validationResult }]
                  : []
              },
              undefined,
              subject
            );
            setJobEventErrors(key, subKey, error[0]?.message);
            return error[0]?.message;
          }
        }
        break;
      }

      case "schedules": {
        if (!subKey) {
          throw "subKey の指定が必須です";
        }
        const targetSchedule = editingJobEvent.value.schedules.find(
          item => item.id === subKey
        );
        if (!targetSchedule) {
          return;
        }

        const getScheduleDataRangeErrorMessage = (subject: string) => {
          const error =
            !!targetSchedule.start_at &&
            !!targetSchedule.end_at &&
            validateDateRange(
              [targetSchedule.start_at || "", targetSchedule.end_at || ""],
              undefined,
              subject
            );

          return error ? error[0]?.message : "";
        };

        switch (key) {
          case "schedules/title": {
            handleWarnMessageForTextarea(key, subKey, targetSchedule.title);
            const validationResult = validateTextarea(
              formConfig[key].label,
              targetSchedule.title
            );
            const error = validate(
              targetSchedule.title,
              {
                required: !forDraft,
                customErrors: validationResult
                  ? [{ message: validationResult }]
                  : []
              },
              undefined,
              formConfig[key].label
            );
            setJobEventErrors(key, subKey, error[0]?.message);
            return error[0]?.message;
          }
          case "schedules/time_limit_at": {
            const error = validate(
              targetSchedule.time_limit_at,
              {
                required: !forDraft,
                isDateAndTime: true,
                customErrors: [
                  ...(!targetSchedule.schedule_id &&
                  isDateBeforeCurrentDate(targetSchedule.time_limit_at)
                    ? [
                        {
                          message: `${formConfig[key].label}は今日以降の日付を入力してください`
                        }
                      ]
                    : []),
                  ...(targetSchedule.start_at &&
                  isDateAfterSpecificDate(
                    targetSchedule.time_limit_at,
                    targetSchedule.start_at
                  )
                    ? [
                        {
                          message: `${formConfig[key].label}はイベント開催日程より前の日時を入力してください`
                        }
                      ]
                    : [])
                ]
              },
              undefined,
              formConfig[key].label
            );
            setJobEventErrors(key, subKey, error[0]?.message);
            return error[0]?.message;
          }
          case "schedules/start_at": {
            setJobEventErrors("schedules/end_at", undefined);

            const error = validate(
              targetSchedule.start_at,
              {
                isDateAndTime: true,
                customErrors: [
                  ...(!targetSchedule.schedule_id &&
                  targetSchedule.start_at &&
                  isDateBeforeCurrentDate(targetSchedule.start_at)
                    ? [
                        {
                          message: `${formConfig[key].label}は今日以降の日付を入力してください`
                        }
                      ]
                    : []),
                  ...(!targetSchedule.schedule_id &&
                  targetSchedule.start_at &&
                  targetSchedule.end_at &&
                  isSameDateAndMinute(
                    targetSchedule.start_at,
                    targetSchedule.end_at
                  )
                    ? [
                        {
                          message: `開催日時の開始と終了に異なる時刻を入力してください`
                        }
                      ]
                    : [])
                ]
              },
              undefined,
              formConfig[key].label
            );

            const dataRangeErrorMessage = getScheduleDataRangeErrorMessage(
              formConfig[key].label
            );

            setJobEventErrors(
              "schedules/start_at",
              subKey,
              error[0]?.message || dataRangeErrorMessage
            );

            return;
          }
          case "schedules/end_at": {
            setJobEventErrors("schedules/start_at", undefined);

            const error = validate(
              targetSchedule.end_at,
              {
                isDateAndTime: true,
                customErrors: [
                  ...(!targetSchedule.schedule_id &&
                  targetSchedule.end_at &&
                  isDateBeforeCurrentDate(targetSchedule.end_at)
                    ? [
                        {
                          message: `${formConfig[key].label}は今日以降の日付を入力してください`
                        }
                      ]
                    : []),
                  ...(!targetSchedule.schedule_id &&
                  targetSchedule.start_at &&
                  targetSchedule.end_at &&
                  isSameDateAndMinute(
                    targetSchedule.start_at,
                    targetSchedule.end_at
                  )
                    ? [
                        {
                          message: `開催日時の開始と終了に異なる時刻を入力してください`
                        }
                      ]
                    : [])
                ]
              },
              undefined,
              formConfig[key].label
            );

            const dataRangeErrorMessage = getScheduleDataRangeErrorMessage(
              formConfig[key].label
            );

            setJobEventErrors(
              "schedules/end_at",
              subKey,
              error[0]?.message || dataRangeErrorMessage
            );

            return;
          }
          case "schedules/schedule_type": {
            if (targetSchedule.schedule_type === "online") {
              // 対面に関するエラーはリセット
              setJobEventErrors("schedules/prefecture", subKey);
              setJobEventErrors("schedules/place", subKey);
              setJobEventErrors("schedules/limited_place", subKey);

              // オンラインに関するエラーを復活
              jobEventValidate("schedules/live_url", subKey);
            } else {
              // オンラインに関するエラーはリセット
              setJobEventErrors("schedules/live_url", subKey);

              // 対面に関するエラーを復活
              jobEventValidate("schedules/prefecture", subKey);
              jobEventValidate("schedules/place", subKey);
              jobEventValidate("schedules/limited_place", subKey);
            }

            const error = validate(
              targetSchedule.schedule_type,
              {
                required: !forDraft
              },
              undefined,
              formConfig[key].label
            );
            setJobEventErrors(key, subKey, error[0]?.message);
            return error[0]?.message;
          }
          case "schedules/live_url": {
            hasWarnUrl(targetSchedule.live_url)
              ? setJobEventWarns(key, subKey, warnUrlMessage)
              : setJobEventWarns(key, subKey, undefined);
            // NOTE: 不要なためコメントアウトするが、必要になったら復活させる
            if (targetSchedule.schedule_type === "offline") {
              setJobEventErrors(key, subKey);
              return;
            }
            const error = validate(
              targetSchedule.live_url,
              {
                required:
                  targetSchedule.schedule_type === "online" && !forDraft,
                isUrl: true
              },
              undefined,
              formConfig[key].label
            );
            setJobEventErrors(key, subKey, error[0]?.message);
            return error[0]?.message;
          }
          case "schedules/admission_limit": {
            const error = validate(
              targetSchedule.admission_limit,
              {
                isNumber: true,
                minCount: -1
              },
              undefined,
              formConfig[key].label
            );
            setJobEventErrors(key, subKey, error[0]?.message);
            return error[0]?.message;
          }

          case "schedules/place": {
            if (targetSchedule.schedule_type === "online") {
              setJobEventErrors(key, subKey);
              return;
            }
            const validationResult = validateTextarea(
              formConfig[key].label,
              targetSchedule.place
            );
            const error = validate(
              targetSchedule.place,
              {
                customErrors: validationResult
                  ? [{ message: validationResult }]
                  : []
              },
              undefined,
              formConfig[key].label
            );
            setJobEventErrors(key, subKey, error[0]?.message);
            return error[0]?.message;
          }

          case "schedules/limited_place": {
            if (!editingJobEvent.value.is_lottery) return;
            if (targetSchedule.schedule_type === "online") {
              setJobEventErrors(key, subKey);
              return;
            }
            const validationResult = validateTextarea(
              formConfig[key].label,
              targetSchedule.limited_place
            );
            const error = validate(
              targetSchedule.limited_place,
              {
                customErrors: validationResult
                  ? [{ message: validationResult }]
                  : []
              },
              undefined,
              formConfig[key].label
            );
            setJobEventErrors(key, subKey, error[0]?.message);
            return error[0]?.message;
          }

          case "schedules/prefecture":
          case "schedules/entry_stopped":
          case "schedules/canceled": {
            return;
          }
        }
        break;
      }

      case "resume_questions": {
        if (!subKey) {
          throw "subKey の指定が必須です";
        }
        const targetResume = editingJobEvent.value.resume_questions.find(
          item => item.id === subKey
        );
        if (!targetResume) {
          return;
        }

        switch (key) {
          case "resume_questions/title": {
            const error = validate(
              targetResume.title,
              {
                required: !forDraft
              },
              undefined,
              formConfig[key].label
            );
            setJobEventErrors(key, subKey, error[0]?.message);
            return;
          }
          case "resume_questions/description": {
            return;
          }
          case "resume_questions/min_length": {
            const error = validate(
              targetResume.min_length,
              {
                required: !forDraft,
                isNumber: !forDraft,
                minCount: 0
              },
              undefined,
              formConfig[key].label
            );
            setJobEventErrors(key, subKey, error[0]?.message);
            return;
          }
        }
        break;
      }
    }

    throw `${key} のハンドリング漏れ、もしくは存在しないkeyです`;
  };

  const setJobEventErrors = (
    key: ValidateKey,
    subKey?: ValidateSubKey,
    message?: string
  ) => {
    const nextErrors = jobEventErrors.value.filter(error =>
      subKey ? error.key !== key || error.subKey !== subKey : error.key !== key
    );

    if (message) {
      nextErrors.push({ key, message, subKey });
    }

    jobEventErrors.value = nextErrors;
  };

  const resetJobEventErrors = () => {
    jobEventErrors.value = [];
  };

  const getJobEventErrorMessage = (
    key: ValidateKey,
    subKey?: string | number
  ) => {
    const error = jobEventErrors.value.find(error =>
      subKey ? error.key === key && error.subKey === subKey : error.key === key
    );

    return error?.message;
  };

  const setJobEventWarns = (
    key: ValidateKey,
    subKey?: ValidateSubKey,
    message?: string
  ) => {
    const nextWarns = jobEventWarns.value.filter(error =>
      subKey ? error.key !== key || error.subKey !== subKey : error.key !== key
    );

    if (message) {
      nextWarns.push({ key, message, subKey });
    }

    jobEventWarns.value = nextWarns;
  };

  /**
   * イベントの卒年/種別が、PR契約の卒年/種別と合致しているか
   */
  const isValidForPromotionContract = (
    contracts: ApiResponse.ContractInformation[]
  ) => {
    // PR契約と紐づいていない場合はtrue
    if (!promotionContracts.value || promotionContracts.value.length === 0)
      return true;

    const currentContract = contracts.find(
      contract => contract.id === editingJobEvent.value?.contract_id
    );
    if (!currentContract) return true;

    // PR契約の中から、イベントの卒年/種別と合致するものを探索
    const matchingPromotionContract = promotionContracts.value.find(
      contract =>
        contract.graduate_year.includes(currentContract.graduate_year) &&
        contract.event_category === editingJobEvent.value?.category
    );
    // 合致するものがあればtrue, なければfalse
    return !!matchingPromotionContract;
  };

  const getJobEventWarningMessage = (
    key: ValidateKey,
    contracts: ApiResponse.ContractInformation[],
    subKey?: ValidateSubKey
  ) => {
    switch (key) {
      case "contract_id":
      case "category":
        // イベント情報がPR契約と合致していたらreturn
        if (isValidForPromotionContract(contracts)) return undefined;

        const promotionContractText =
          promotionContracts.value
            ?.map(
              contract =>
                `${contract.graduate_year} ${getEventCategoryText?.(
                  contract.event_category
                )}`
            )
            .join(" / ") || "";

        const target = formConfig[key].label;

        return `このイベントは「${promotionContractText}」のPR枠に設定されています。
          ${target}が違うとPR表示されなくなるため、変更したい場合は事前に弊社担当にお問い合わせください。
          弊社担当と合意済みの場合、そのまま掲載依頼をしてください。`;
      case "title":
        if (hasWarnUrl(editingJobEvent.value?.title)) {
          return warnUrlMessage;
        }
        if (
          wordData.value?.competitors_names.some(word =>
            editingJobEvent.value?.title?.includes(word)
          )
        ) {
          return warnCompetitorTextMessage;
        }

        return undefined;
      case "event_description_items/label":
        for (let i = 0; i < displayDescriptionItems.value.length; i++) {
          const item = displayDescriptionItems.value[i];
          if (item?.id !== subKey) {
            continue;
          }
          if (hasWarnUrl(item?.label)) {
            return warnUrlMessage;
          }
          if (
            wordData.value?.competitors_names.some(word =>
              item?.label.includes(word)
            )
          ) {
            return warnCompetitorTextMessage;
          }
        }
        return undefined;
      case "event_description_items/content":
        for (let i = 0; i < displayDescriptionItems.value.length; i++) {
          const item = displayDescriptionItems.value[i];
          if (item?.id !== subKey) {
            continue;
          }
          if (hasWarnUrl(item?.content)) {
            return warnUrlMessage;
          }
          if (
            wordData.value?.competitors_names.some(word =>
              item?.content.includes(word)
            )
          ) {
            return warnCompetitorTextMessage;
          }
        }
        return undefined;
      case "schedules/title":
        if (!editingJobEvent.value?.schedules) return undefined;
        for (let i = 0; i < editingJobEvent.value?.schedules.length; i++) {
          const schedule = editingJobEvent.value?.schedules[i];
          if (schedule?.id !== subKey) {
            continue;
          }
          if (hasWarnUrl(schedule?.title)) {
            return warnUrlMessage;
          }
          if (
            wordData.value?.competitors_names.some(word =>
              schedule?.title.includes(word)
            )
          ) {
            return warnCompetitorTextMessage;
          }
        }
        return undefined;
      case "schedules/place":
        if (!editingJobEvent.value?.schedules) return undefined;
        for (let i = 0; i < editingJobEvent.value?.schedules.length; i++) {
          const schedule = editingJobEvent.value?.schedules[i];
          if (schedule?.id !== subKey) {
            continue;
          }
          if (hasWarnUrl(schedule?.place)) {
            return warnUrlMessage;
          }
          if (
            wordData.value?.competitors_names.some(word =>
              schedule?.place?.includes(word)
            )
          ) {
            return warnCompetitorTextMessage;
          }
        }
        return undefined;
      case "schedules/limited_place":
        if (!editingJobEvent.value?.schedules) return undefined;
        for (let i = 0; i < editingJobEvent.value?.schedules.length; i++) {
          const schedule = editingJobEvent.value?.schedules[i];
          if (schedule?.id !== subKey) {
            continue;
          }
          if (hasWarnUrl(schedule?.limited_place)) {
            return warnUrlMessage;
          }
          if (
            wordData.value?.competitors_names.some(word =>
              schedule?.limited_place?.includes(word)
            )
          ) {
            return warnCompetitorTextMessage;
          }
        }
        return undefined;
      case "schedules/live_url":
        if (!editingJobEvent.value?.schedules) return undefined;
        for (let i = 0; i < editingJobEvent.value?.schedules.length; i++) {
          const schedule = editingJobEvent.value?.schedules[i];
          if (hasWarnUrl(schedule?.live_url) && schedule?.id === subKey) {
            return warnUrlMessage;
          }
          if (
            isNotWebMeetingUrl(schedule?.live_url) &&
            schedule?.id === subKey
          ) {
            return warnWebMeetingMessage;
          }
        }
        return undefined;
      default:
        return undefined;
    }
  };

  const contracts = computed<ApiResponse.Contract[]>(() => {
    const contracts = partnerUser.value?.partner.contracts || [];
    return contracts;
  });

  const isShowIWebSetting = computed<boolean>(() => {
    if (!editingJobEvent.value) {
      return false;
    }

    const selectedContracts = contracts.value.find(
      contract => contract.id === editingJobEvent.value?.contract_id
    );
    return !!selectedContracts && checkAtsApproved(selectedContracts);
  });

  const hasOnlineSchedule = computed<boolean>(() => {
    if (!editingJobEvent.value) {
      return false;
    }

    return editingJobEvent.value.schedules.some(
      schedule => schedule.schedule_type === "online"
    );
  });

  const companyName = partnerUser.value?.partner.company.name || "";

  const defaultMailTemplates: {
    key: JobEventScheduleMailKey;
    name: string;
    description: string;
    default: string;
    /** ユーザ自身が変更 ON/OFF 変更できる項目か */
    disabled: boolean;
  }[] = [
    // 抽選ありのときのみON
    {
      key: "entry",
      name: "エントリー抽選あり",
      description:
        "エントリーした際に送信されるメールです。エントリーのお礼および、抽選制の旨を記載しています。",
      default: `\${user_name}様

こんにちは。
${companyName} 採用担当です。

このたびは『\${event_title}』へエントリー頂き誠にありがとうございました。

\${schedule_info}

本イベントは応募者多数の場合抽選となりますのでご了承いただけますと幸いでございます。

また、お申し込みいただいたイベントの詳細情報は、下記リンクよりご確認いただけます。
\${event_url}

\${cancel_remarks}

お申し込みいただきありがとうございました。
今後ともどうぞ宜しくお願い致します。`,
      disabled: true
    },
    // 抽選ありのときOFF
    {
      key: "pass",
      name: "エントリー抽選なし",
      description:
        "エントリーを行った際に送信されるメールです。エントリーのお礼および、イベントの詳細を記載しています。",
      default: `\${user_name}様

こんにちは。
${companyName} 採用担当です。

このたびは『\${event_title}』へエントリー頂き誠にありがとうございました。

\${schedule_info}

また、お申し込みいただいたイベントの詳細情報は、下記リンクよりご確認いただけます。
\${event_url}

\${cancel_remarks}

お申し込みいただきありがとうございました。
今後ともどうぞ宜しくお願い致します。`,
      disabled: true
    },
    // 抽選ありのときのみON
    {
      key: "elected",
      name: "当選",
      description: "抽選ありのイベントの当選連絡です。",
      default: `\${user_name}様

こんにちは。
${companyName} 採用担当です。

先日は『\${event_title}』へエントリー頂き誠にありがとうございました。
多くのご応募をいただき抽選させていただきました結果、
\${user_name}様は【当選】となりましたので、是非ご参加いただきたく思っております。

\${schedule_info}

\${cancel_remarks}

以上でございます。
当日会場にて皆様にお会いできるのを楽しみにしております。`,
      disabled: true
    },
    // 抽選ありのときのみON
    {
      key: "defeated",
      name: "落選",
      description: "抽選ありのイベントの当選連絡です。",
      default: `\${user_name}様

こんにちは。
${companyName} 採用担当です。

先日は『\${event_title}』へエントリー頂き誠にありがとうございました。
誠に残念ではございますが、抽選させていただきました結果、
この度の\${user_name}様のご参加を見送らせていただくこととなりました。

貴意に添えぬ結果となりましたこと
大変申し訳ございませんが、何卒ご了承くださいますようお願い申し上げます。

今後もONE CAREERでは、特別セミナーや選考枠のご案内を差し上げますので、
是非またエントリーしていただけますと幸いです。`,
      disabled: true
    },
    // 常にトグル
    {
      key: "reminder",
      name: "リマインド",
      description:
        "イベント開催の前日午前10時に送られるリマインドメールです。送信にはスケジュールの開始日時が入力されている必要があります。",
      default: `※ 本メールはワンキャリアよりエントリーした方全員に送信されます。
　 参加に伴い主催企業側にて選考や抽選がある場合には企業からの参加案内をご確認ください。

\${user_name}様

こんにちは。
${companyName} 採用担当です。

明日は『\${event_title}』の開催日です。
イベントの詳細をご確認ください。

\${schedule_info}

また、イベントの詳細情報は、下記リンクよりご確認いただけます。
\${event_url}

\${cancel_remarks}`,
      disabled: false
    },
    // 常にトグル
    {
      key: "thanks",
      name: "参加御礼",
      description:
        "イベント終了の翌日午前10時に送られる参加御礼メールです。送信にはスケジュールの終了日時が入力されている必要があります。",
      default: `※ 本メールはワンキャリアよりエントリーした方全員に送信されます。
　 恐れ入りますが、不要な案内が届いている場合がございます。ご了承下さいませ。

\${user_name}様

いつもお世話になっております。
${companyName} 採用担当です。

昨日は『\${event_title}』へご参加いただきまして、誠にありがとうございました。本イベントが、就職活動の一助になれば幸いです。`,
      disabled: false
    },
    // 常にON
    {
      key: "user_canceled",
      name: "ユーザによるキャンセル",
      description: "イベント参加キャンセル行なった際に送信されるメールです。",
      default: `\${user_name}様

こんにちは。
${companyName} 採用担当です。

『\${event_title}』へのキャンセルを承りました。
キャンセル回数が多くなると、今後のセミナーでは他の希望者が優先されたり、
予約を制限させていただく場合がございますのでご了承ください。

お申し込みいただきありがとうございました。
今後ともどうぞ宜しくお願い致します。`,
      disabled: true
    }
  ];

  return {
    isNewJobEvent,
    originalEvent,
    editingJobEvent,
    editingJobEventSchedules,
    setEditingJobEvent,
    comments,
    promotionContracts,
    uncroppedImage,
    requiredDescriptionItemIds,
    optionalDefaultDescriptionItemIds,
    repeatedDescriptionItemIds,
    disabledEdit,
    disabledReceptionTypeEdit,
    displayDescriptionItems,
    setLackedRequiredDescriptionItems,
    setLackedOptionalDefaultDescriptionItems,

    refreshEditingJobEvent,
    saveJobEvent,
    submitJobEvent,
    withdrawJobEvent,
    loading,

    jobEventErrors,
    jobEventWarns,
    jobEventValidate,
    validateAllProperties,
    resetDescriptionError,
    resetScheduleError,
    resetResumeError,
    getJobEventErrorMessage,
    setJobEventErrors,
    getJobEventWarningMessage,

    formErrors,
    getFirstErrorMessage,
    resetErrorMessage,
    validate,

    contracts,
    isShowIWebSetting,
    hasOnlineSchedule,

    defaultMailTemplates
  } as const;
};
